import React, { useState, useEffect } from 'react';

const ColumnVisibilityModal = ({
  isOpen,
  onClose,
  columnVisibility,
  setColumnVisibility,
  defaultVisibility,
  columnMeta = {} // { columnId: { title, subtitle } }
}) => {
  // Create local state that mirrors the parent state
  const [localVisibility, setLocalVisibility] = useState(columnVisibility || {});
  
  // Update local state when parent state changes
  useEffect(() => {
    if (columnVisibility) {
      setLocalVisibility(columnVisibility);
    }
  }, [columnVisibility]);
  
  // Close modal and save changes
  const handleSave = () => {
    // Update parent state with local changes
    if (setColumnVisibility) {
      setColumnVisibility(localVisibility);
    }
    onClose();
  };
  
  // Close modal without saving changes
  const handleCancel = () => {
    // Reset local state to match parent state
    setLocalVisibility(columnVisibility);
    onClose();
  };

  if (!isOpen) return null;

  // Toggle a column in local state only
  const toggleColumn = (columnId) => {
    setLocalVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Select All -> set all columns to true in local state
  const handleSelectAll = () => {
    const allSelected = Object.keys(localVisibility).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setLocalVisibility(allSelected);
  };

  // Reset -> either use defaultVisibility or set all to false in local state
  const handleReset = () => {
    if (defaultVisibility) {
      setLocalVisibility(defaultVisibility);
    } else {
      const allFalse = Object.keys(localVisibility).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      setLocalVisibility(allFalse);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      {/* Modal container: 420x620 */}
      <div className="bg-white w-[420px] h-[620px] rounded-lg shadow-lg flex flex-col overflow-hidden">
        
        {/* Header: 420x100 */}
        <div className="relative h-[100px] bg-[#0832DE] flex flex-col items-center justify-center rounded-t-lg">
          {/* Close Button */}
          <button
            onClick={handleCancel}
            className="absolute right-4 top-1/2 -translate-y-1/2 focus:outline-none"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 6L12 12L18 6"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18 18L12 12L6 18"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {/* Centered Text */}
          <div className="text-white text-[18px] font-semibold">Настройки параметра</div>
          <div className="text-white text-[18px] font-semibold">"Skills"</div>
        </div>
        
        {/* Body: Centered container of 370x350 */}
        <div className="flex-1 flex justify-center items-start px-4 pt-4">
          <div className="w-[370px] h-[350px] overflow-y-auto border border-gray-200 rounded p-2">
            {Object.keys(localVisibility).map((columnId) => {
              const meta = columnMeta[columnId] || {};
              const title = meta.title || columnId;
              const subtitle = meta.subtitle || '';
              return (
                <div
                  key={columnId}
                  className="flex items-center justify-between py-2 border-b last:border-b-0 border-gray-200"
                >
                  {/* Left: Title & Subtitle */}
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-900">{title}</span>
                    {subtitle && (
                      <span className="text-xs text-gray-500">{subtitle}</span>
                    )}
                  </div>
                  {/* Right: Checkbox */}
                  <label
                    className="inline-flex w-4 h-4 cursor-pointer"
                    onClick={() => toggleColumn(columnId)}
                  >
                    {localVisibility[columnId] ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="16" height="16" rx="4" fill="#0832DE" />
                        <g clipPath="url(#clip0_15471_17629)">
                          <path
                            d="M12 5.5L6.5 11L4 8.5"
                            stroke="white"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_15471_17629">
                            <rect
                              width="12"
                              height="12"
                              fill="white"
                              transform="translate(2 2)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="0.5"
                          y="0.5"
                          width="15"
                          height="15"
                          rx="3.5"
                          stroke="#0832DE"
                        />
                      </svg>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between px-4 pb-4">
          <div className="flex space-x-2">
            <button
              onClick={handleSelectAll}
              aria-label="Выбрать все"
              className="flex items-center gap-1 bg-gray-200 border border-gray-300 px-3 py-1.5 rounded"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.66732 8.00163L7.96712 11.3014L15.0388 4.23047M1.36719 8.03516L4.66702 11.335M11.738 4.264L8.20245 7.79956"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              onClick={handleReset}
              aria-label="Сбросить отображение до значений по умолчанию"
              className="flex items-center gap-1 bg-gray-200 border border-gray-300 px-3 py-1.5 rounded"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 10.668C9.114 10.668 10 9.78064 10 8.66797C10 7.5553 9.114 6.66797 8 6.66797C6.886 6.66797 6 7.5553 6 8.66797C6 9.78064 6.886 10.668 8 10.668Z"
                  fill="black"
                />
                <path
                  d="M13.878 7.45603C13.7209 6.68827 13.4139 5.95902 12.9747 5.31003C12.5431 4.67155 11.9931 4.12165 11.3547 3.69003C10.7056 3.25092 9.97638 2.94396 9.20867 2.7867C8.8054 2.70492 8.39481 2.66471 7.98333 2.6667V1.33203L5.33333 3.33203L7.98333 5.33203V4.00003C8.306 3.9987 8.62867 4.02936 8.94 4.09336C9.53668 4.21564 10.1035 4.45417 10.608 4.79536C11.1056 5.13086 11.5338 5.55911 11.8693 6.0567C12.3902 6.82685 12.668 7.7356 12.6667 8.66536C12.6666 9.28929 12.5419 9.90691 12.3 10.482C12.1824 10.7591 12.0386 11.0243 11.8707 11.274C11.7021 11.5223 11.5106 11.7542 11.2987 11.9667C10.6533 12.6108 9.83407 13.0527 8.94133 13.238C8.32048 13.3634 7.68085 13.3634 7.06 13.238C6.46304 13.1156 5.89603 12.8769 5.39133 12.5354C4.89432 12.2002 4.46653 11.7724 4.13133 11.2754C3.61104 10.5044 3.33314 9.59547 3.33333 8.66536H2C2.00071 9.86116 2.35792 11.0296 3.026 12.0214C3.4579 12.6588 4.00724 13.2081 4.64467 13.64C5.63508 14.3103 6.80407 14.6676 8 14.6654C8.40618 14.6653 8.81133 14.6245 9.20933 14.5434C9.97649 14.385 10.7053 14.0781 11.3547 13.64C11.6735 13.4252 11.9707 13.1798 12.242 12.9074C12.5137 12.6349 12.7592 12.3376 12.9753 12.0194C13.6451 11.0292 14.0021 9.86075 14 8.66536C14 8.25918 13.9591 7.85404 13.878 7.45603Z"
                  fill="black"
                />
              </svg>
            </button>
          </div>
          <button
            onClick={handleSave}
            className="text-white bg-[#0832DE] w-[150px] h-[40px] rounded"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnVisibilityModal;